import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { TeamMember, Traveler } from '@marageti/z4-sdk/lib/people';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import {
  TripMatch, TripRequest, Partner, SalesRecording,
} from '@marageti/z4-sdk/lib/travel';
import {
  Assignee,
  Ticket,
  TicketStatus,
  TicketComment,
} from '@marageti/z4-sdk/lib/tickets';
import {
  useApiClient,
  useFetch,
  useFetchImmutable,
  formatCountryCodesToString,
  getFormattedDateShortMonthDDYYYY,
} from '@marageti/z4-lib';
import { TicketReassignFormData } from '../../components/modals/ticket-reassign-modal';
import { TicketFormData, AssigneeOption } from '../tickets/options';
import { TicketCloseFormData } from '../../components/modals/ticket-close-modal';
import {
  formatTeamMembersAsOptions, teamMemberSearchRequest,
} from '../trip-requests/options';
import { SelectedTab } from '../../components/travel-history-modal';
import TicketDetailView from './ticket-detail-view';
import useTicketComments from './use-ticket-comments';
import { useAuthStore } from '../../store';

const TicketDetailContainer = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const { loggedInTeamMember } = useAuthStore();

  const {
    data: ticket,
    isLoading,
    mutate: mutateTicket,
  } = useFetch<Ticket>(
    apiClient.ticketsClient.getTicket,
    [ticketId],
    !!ticketId,
  );

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isReassignModalOpen, setReassignModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: teamMembers } = useFetch<SearchResult<TeamMember>>(
    apiClient.peopleClient.postTeamMemberSearch,
    [teamMemberSearchRequest],
    true,
  );

  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    true,
  );

  const assignees = useMemo<AssigneeOption[]>(() => {
    const options = formatTeamMembersAsOptions(teamMembers, []);
    return options.filter((option) => option.value);
  }, [teamMembers]);

  const { data: travelerData } = useFetch<Traveler>(
    apiClient.peopleClient.getTraveler,
    [ticket?.travelerId || ''],
    !!ticket?.travelerId,
  );

  const [isTravelHistoryOpen, setTravelHistoryOpen] = useState(false);
  const [travelHistoryTab, setTravelHistoryTab] = useState<SelectedTab>('trip-requests');

  const handleOpenTravelHistory = useCallback(() => {
    setTravelHistoryOpen(true);
  }, []);

  const handleCloseTravelHistory = useCallback(() => {
    setTravelHistoryOpen(false);
  }, []);

  const { data: tripRequestData } = useFetch<TripRequest>(
    apiClient.tripRequestsClient.getTripRequest,
    [ticket?.tripRequestId || ''],
    !!ticket?.tripRequestId,
  );

  const { data: tripMatchesData } = useFetch<TripMatch[]>(
    apiClient.tripRequestsClient.getTripRequestTripMatches,
    [ticket?.tripRequestId || ''],
    !!ticket?.tripRequestId,
  );

  const tripRequestLabel = useMemo(() => {
    if (!tripRequestData || !countryCodes) return '';
    const destinations = formatCountryCodesToString(tripRequestData.countries, countryCodes);
    const departureDate = getFormattedDateShortMonthDDYYYY(tripRequestData.departureDate);
    return `${destinations} - ${departureDate}`;
  }, [tripRequestData, countryCodes]);

  const tripMatch = useMemo(() => {
    if (!tripMatchesData || !ticket?.tripMatchId) return undefined;
    return tripMatchesData.find((tm) => tm.id === ticket.tripMatchId);
  }, [tripMatchesData, ticket?.tripMatchId]);

  const tripMatchLabel = useMemo(() => {
    if (!tripMatch || !countryCodes) return '';
    const destinations = formatCountryCodesToString(tripMatch.countries, countryCodes);
    const departureDate = getFormattedDateShortMonthDDYYYY(tripMatch.departureDate);
    return `${destinations} - ${departureDate}`;
  }, [tripMatch, countryCodes]);

  const { data: partnerData } = useFetch<Partner>(
    apiClient.partnersClient.getPartner,
    [tripMatch?.partnerId || ''],
    !!tripMatch?.partnerId,
  );

  const { data: salesRecordings } = useFetch<SalesRecording[]>(
    apiClient.tripMatchesClient.getTripMatchSalesRecordings,
    [tripMatch?.id || ''],
    !!tripMatch?.id,
  );

  const totalCommissionUSD = useMemo(() => {
    if (!salesRecordings || salesRecordings.length === 0) return undefined;
    return salesRecordings.reduce((sum, sr) => sum + (sr.commission?.amountUSD || 0), 0);
  }, [salesRecordings]);

  const {
    comments,
    isLoading: isCommentsLoading,
    hasMore: hasMoreComments,
    loadMore: loadMoreComments,
    createComment,
    createFileComment,
    updateComment,
    mutate: mutateTicketComments,
  } = useTicketComments(ticketId);

  const commentsRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleTicketCommentsRefetch = useCallback(() => {
    if (commentsRefetchTimeoutRef.current) {
      clearTimeout(commentsRefetchTimeoutRef.current);
    }
    commentsRefetchTimeoutRef.current = setTimeout(() => {
      commentsRefetchTimeoutRef.current = null;
      Promise.resolve(mutateTicketComments()).catch(() => {});
    }, 400);
  }, [mutateTicketComments]);

  useEffect(() => () => {
    if (commentsRefetchTimeoutRef.current) {
      clearTimeout(commentsRefetchTimeoutRef.current);
    }
  }, []);

  const handleDownloadFile = useCallback(async (comment: TicketComment) => {
    if (!ticketId || !comment.fileId) return;
    try {
      const result = await apiClient.ticketsClient.getTicketDocumentDownloadUrl(ticketId, comment.fileId);
      window.open(result.url, '_blank');
    } catch {
      enqueueSnackbar('Failed to download file.', { variant: 'default' });
    }
  }, [ticketId, apiClient, enqueueSnackbar]);

  const handleOpenEdit = () => {
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
  };

  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!ticketId || !ticket) return;
    try {
      await apiClient.ticketsClient.updateTicketStatus(ticketId, {
        status: newStatus as TicketStatus,
      });
      mutateTicket();
      scheduleTicketCommentsRefetch();
      enqueueSnackbar('Ticket status updated.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to update status.', { variant: 'default' });
    }
  }, [ticketId, ticket, apiClient, enqueueSnackbar, mutateTicket, scheduleTicketCommentsRefetch]);

  const handleCloseTicket = useCallback(async (data: TicketCloseFormData) => {
    if (!ticketId) return;
    try {
      await apiClient.ticketsClient.updateTicketStatus(ticketId, {
        status: 'COMPLETED',
        outcomeSeverity: data.outcomeSeverity!.value,
        outcomeComments: data.outcomeComments,
        zicassoCreditAmount: data.zicassoCreditEnabled ? Number(data.zicassoCreditAmount) : undefined,
        partnerRefundAmount: data.partnerRefundEnabled ? Number(data.partnerRefundAmount) : undefined,
      });
      mutateTicket();
      scheduleTicketCommentsRefetch();
      enqueueSnackbar('Ticket closed.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to close ticket.', { variant: 'default' });
    }
  }, [ticketId, apiClient, enqueueSnackbar, mutateTicket, scheduleTicketCommentsRefetch]);

  const handleSaveTicket = async (data: TicketFormData) => {
    if (!ticketId) return;
    const ticketAssignees: Assignee[] = data.assignees.map((a) => ({
      id: a.value,
      name: a.title,
    }));
    try {
      await apiClient.ticketsClient.updateTicket(ticketId, {
        title: data.title,
        description: data.description,
        ticketSubType: data.type?.value,
        status: data.status.value,
        travelerId: data.traveler?.id || '',
        travelerName: data.traveler?.fullName || '',
        assignees: ticketAssignees,
        tripRequestId: data.tripRequest?.value,
        tripMatchId: data.tripMatch?.value || '',
      });
      enqueueSnackbar('Ticket updated.', { variant: 'success' });
      handleCloseEdit();
      mutateTicket();
      scheduleTicketCommentsRefetch();
    } catch {
      enqueueSnackbar('Failed to update ticket.', { variant: 'default' });
    }
  };

  const handleOpenReassign = () => setReassignModalOpen(true);
  const handleCloseReassign = () => setReassignModalOpen(false);

  const handleSaveReassign = async (data: TicketReassignFormData) => {
    if (!ticketId || !ticket) return;
    const ticketAssignees: Assignee[] = data.assignees.map((a) => ({
      id: a.value,
      name: a.title,
    }));
    try {
      await apiClient.ticketsClient.updateTicket(ticketId, {
        title: ticket.title,
        description: ticket.description,
        status: ticket.status,
        ticketSubType: ticket.ticketSubType,
        travelerId: ticket.travelerId,
        travelerName: ticket.travelerName,
        tripRequestId: ticket.tripRequestId,
        tripMatchId: ticket.tripMatchId,
        assignees: ticketAssignees,
      });
      enqueueSnackbar('Ticket updated.', { variant: 'success' });
      handleCloseReassign();
      mutateTicket();
      scheduleTicketCommentsRefetch();
    } catch {
      enqueueSnackbar('Failed to reassign ticket.', { variant: 'default' });
    }
  };

  const handleOpenDelete = () => setDeleteModalOpen(true);
  const handleCloseDelete = () => setDeleteModalOpen(false);

  const handleConfirmDelete = async () => {
    if (!ticketId) return;
    try {
      await apiClient.ticketsClient.updateTicketStatus(ticketId, { status: 'CANCELED' });
      enqueueSnackbar('Ticket deleted.', { variant: 'success' });
      handleCloseDelete();
      navigate('/tickets');
    } catch {
      enqueueSnackbar('Failed to delete ticket.', { variant: 'default' });
    }
  };

  return (
    <TicketDetailView
      ticket={ticket}
      traveler={travelerData}
      isLoading={isLoading}
      isEditModalOpen={isEditModalOpen}
      assignees={assignees}
      tripRequestLabel={tripRequestLabel}
      tripMatch={tripMatch}
      tripMatchLabel={tripMatchLabel}
      partnerCrmProfile={partnerData?.crmProfile}
      totalCommissionUSD={totalCommissionUSD}
      isTravelHistoryOpen={isTravelHistoryOpen}
      travelHistoryTab={travelHistoryTab}
      onTravelHistoryTabChange={setTravelHistoryTab}
      onOpenTravelHistory={handleOpenTravelHistory}
      onCloseTravelHistory={handleCloseTravelHistory}
      onStatusChange={handleStatusChange}
      onCloseTicket={handleCloseTicket}
      onOpenEdit={handleOpenEdit}
      onCloseEdit={handleCloseEdit}
      onSaveTicket={handleSaveTicket}
      onReassign={handleOpenReassign}
      onDelete={handleOpenDelete}
      isReassignModalOpen={isReassignModalOpen}
      reassigningTicketAssigneeIds={ticket?.assignees?.map((a) => a.id) || []}
      onCloseReassignModal={handleCloseReassign}
      onSaveReassign={handleSaveReassign}
      isDeleteModalOpen={isDeleteModalOpen}
      onCloseDeleteModal={handleCloseDelete}
      onConfirmDelete={handleConfirmDelete}
      comments={comments}
      isCommentsLoading={isCommentsLoading}
      hasMoreComments={hasMoreComments}
      currentUserId={loggedInTeamMember?.id}
      onLoadMoreComments={loadMoreComments}
      onCreateComment={createComment}
      onCreateFileComment={createFileComment}
      onUpdateComment={updateComment}
      onDownloadFile={handleDownloadFile}
    />
  );
};

export default TicketDetailContainer;
